import { useCallback, useState } from 'react';

import {
  useDeleteGroupModuleDataMutation,
  useDeleteGroupContentMutation,
  GetContentAllGroupsDocument,
  GroupContentFragmentDoc,
  useCreateNewGroupContentMutation,
  useUpdateGroupContentMutation,
  useGetContentAllGroupsLazyQuery,
  useUpdateVideoGroupContentMutation
} from '@api/graphql-codegen';
import { DefaultModuleEnum, ExtendedContent } from '@types';
import { useEditUnit } from '../useEditUnit';
import { logger } from '../../../../../utils/logger';

export interface useSchoolGroupsModuleCRUDProps {
  currentTabContent?: ExtendedContent;
  groups: ExtendedContent[];
  clearFile: () => void;
  activeTab: string;
  isGroupContent: boolean;
  onChangeData: (v: string | null) => void;
  onChangeGroupTabs: (v: ExtendedContent[]) => void;
  onChangeTab: (key: string) => void;
};

export const useSchoolGroupsModuleCRUD = ({ currentTabContent, groups, activeTab, clearFile, onChangeData, onChangeGroupTabs, onChangeTab }: useSchoolGroupsModuleCRUDProps) => {
  const { content } = useEditUnit();
  const [isOpenAddModal, setOpenAddModal] = useState<boolean>(false);

  const [getContentsByGroup, { loading: isFetchingAllGroups }] = useGetContentAllGroupsLazyQuery({ fetchPolicy: 'network-only' });
  const [deleteGroupModuleData, { loading: deletingGroupModuleData }] = useDeleteGroupModuleDataMutation({
    update(cache, res) {
      if (res.data?.deleteGroupModuleData) {
        cache.updateFragment({
          id: `GroupContent:{"contentId":"${currentTabContent?.id}","groupId":"${currentTabContent?.groupId}"} `,
          fragment: GroupContentFragmentDoc,
        }, (c) => ({ ...c, data: null }))
        onChangeData(null);
        const newGroups = [...groups];
        const index = newGroups.findIndex(g => g.groupId === currentTabContent?.groupId);
        newGroups[index].data = null;
        onChangeGroupTabs(newGroups);
      }
    }
  });

  const [deleteGroupContent, { loading: isGroupRemoving }] = useDeleteGroupContentMutation({
    update(cache, res) {
      if (res.data?.deleteGroupContent) {
        const defaultContent = groups[0];
        let updatingGroups: any[] = groups.slice(1);
        const index = updatingGroups.findIndex(a => a.groupName === activeTab);
        if (index > -1) {
          updatingGroups.splice(index, 1);
        }
        cache.writeQuery({
          query: GetContentAllGroupsDocument,
          data: {
            getContentAllGroups: {
              __typename: "AllContent",
              groups: updatingGroups
            }
          },
          variables: {
            contentId: content?.id
          }
        });
        onChangeGroupTabs([defaultContent, ...updatingGroups]);
        onChangeTab(DefaultModuleEnum.Default);
      }
    },
    onError(e) {
      logger.info('e--', e);
    },
  });

  const [createNewGroupContentMutation, { loading: isCreatingGroup }] = useCreateNewGroupContentMutation({
    update(cache, res) {
      if (res.data?.createNewGroupContent && groups.length > 0) {
        let updatingGroups: any[] = groups.slice(1);
        if (updatingGroups.findIndex(u => u.groupId === res.data?.createNewGroupContent?.groupId) === -1) {
          updatingGroups = [...updatingGroups, res.data?.createNewGroupContent];
        }
        cache.writeQuery({
          query: GetContentAllGroupsDocument,
          data: {
            getContentAllGroups: {
              __typename: "AllContent",
              groups: updatingGroups
            }
          },
          variables: {
            contentId: res.data?.createNewGroupContent?.contentId
          }
        });
      }
    }
  });

  const [updateGroupContent, { loading: isUpdatingGroupContent }] = useUpdateGroupContentMutation({
    update(__cache, res) {
      const updatedGroupContent = res.data?.updateGroupContent;
      if (updatedGroupContent) {
        let updatingGroups: any[] = [...groups.slice(1)];
        const index = updatingGroups.findIndex(u => u.groupId === updatedGroupContent?.groupId);
        if (index > -1) {
          updatingGroups[index] = {...updatingGroups[index], ...updatedGroupContent};
        }
        onChangeGroupTabs([groups[0], ...updatingGroups]);
        clearFile();
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });

  const [updateVideoGroupContent, { loading: isUpdatingGroupVideoContent }] = useUpdateVideoGroupContentMutation({
    update(__cache, res) {
      const updatedGroupContent = res.data?.updateVideoGroupContent;
      if (updatedGroupContent) {
        let updatingGroups: any[] = [...groups.slice(1)];
        const index = updatingGroups.findIndex(u => u.groupId === updatedGroupContent?.groupId);
        if (index > -1) {
          updatingGroups[index] = {...updatingGroups[index], ...updatedGroupContent};
        }
        const newGroups = [groups[0], ...updatingGroups];
        onChangeData(updatedGroupContent?.data ?? '');
        onChangeGroupTabs(newGroups);
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });

  const handleRemoveGroup = useCallback(async () => {
    if (window.confirm(`Are you sure you want to remove ${currentTabContent?.groupName} group in the Module?`)) {
      const id = currentTabContent?.id || currentTabContent?.contentId
      if (id) {
        await deleteGroupContent({
          variables: {
            input: {
              id,
              group: currentTabContent?.groupId
            }
          }
        })
      }
    }
  }, [currentTabContent, deleteGroupContent]);

  const handleAddGroup = useCallback(() => {
    setOpenAddModal(true);
  }, []);
  const onCloseAddGroupModal = useCallback(() => {
    setOpenAddModal(false)
  }, []);

  return {
    getContentsByGroup,
    createNewGroupContentMutation,
    updateGroupContent,
    deleteGroupModuleData,
    deleteGroupContent,
    updateVideoGroupContent,
    isGroupRemoving: isGroupRemoving || deletingGroupModuleData,
    isGroupsLoading: isFetchingAllGroups || isCreatingGroup || isUpdatingGroupContent || isUpdatingGroupVideoContent || deletingGroupModuleData,
    handleRemoveGroup,
    isOpenAddModal,
    handleAddGroup,
    onCloseAddGroupModal,
  }
};
